import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';

import hooks from '@g4/automation-hooks';

import { Box, Text, ConditionalWrap } from '../Boneyard';

export const propTypes = {
  /** Input field `name` attribute value */
  name: PropTypes.string,
  /** The value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The defaut value */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * HTML input type
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types
   */
  type: PropTypes.string.isRequired,
  /** Creates an associated label element */
  label: PropTypes.string,
  /**
   * Indicates that the field is required
   *
   * Depends a `label` value
   */
  isRequired: PropTypes.bool,
  /** Text to display whilst a value hasn't been selected */
  placeholder: PropTypes.string,
  /** Display a border around the input */
  withBorder: PropTypes.bool,
  /** Prevents text from being entered */
  disabled: PropTypes.bool,
  /** Applies error styling */
  hasError: PropTypes.bool,
  /** Icon to display inside the input area */
  icon: PropTypes.element,
  /**
   * Hides the spin button controls for numeric input fields
   *
   * MDN Warning: This feature is non-standard and is not on a standards track.
   * Do not use it on production sites facing the Web: it will not work for every user.
   */
  hideSpinControls: PropTypes.bool,
  /** A ref to the <input> element */
  inputRef: PropTypes.object,
  /** onchange callback handler */
  onChange: PropTypes.func,
  /** onblur challback handler */
  onBlur: PropTypes.func,
  /** onfocus challback handler */
  onFocus: PropTypes.func,
  /** The maximum number of characters allowed to be entered */
  maxLength: PropTypes.number,
  /** Prevents users from modifying the field value */
  readOnly: PropTypes.bool,
  /** A regular expression the input's contents must match in order to be valid */
  pattern: PropTypes.string,
  /**
   * HTML `autocomplete` attribute
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete: PropTypes.string,
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  theme: PropTypes.object,
  className: PropTypes.string,
  labelSize: PropTypes.number,
};

export const defaultProps = {
  withBorder: true,
  onChange: Function.prototype,
  onBlur: Function.prototype,
  labelSize: 14,
};

export const IconContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 50px;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;

  /* prevent assets from exceeding the bounds of the container */
  svg,
  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

export const Asterisk = styled.span.attrs({
  children: <React.Fragment>&lowast;</React.Fragment>,
})`
  position: relative;
  top: -3px;
  color: ${props => props.theme.colors.input.required};
  margin-left: ${props => props.theme.spacing(1)};
`;

const ClickableWrapper = styled.div`
  cursor: ${props => (props.disabled ? 'initial' : 'pointer')};
`;

// prettier-ignore
const Input = styled(withTheme(props => (
  <div>
    {props.label && (
      <Box mb={2}>
        <label htmlFor={props.name}>
          <Text size={props.labelSize} weight={500} color={props.theme.colors.input.label}>
            {props.label}
            {props.isRequired && <Asterisk />}
          </Text>
        </label>
      </Box>
    )}

    <Box position="relative">
      <ConditionalWrap
        condition = {props.onClick}
        wrap={ children => <ClickableWrapper onClick={props.onClick} disabled={props.disabled}>{children}</ClickableWrapper> }
      >
        <input
          ref={props.inputRef}
          name={props.name}
          type={props.type}
          id={props.id}
          value={props.value}
          defaultValue={props.defaultValue}
          placeholder={props.placeholder}
          disabled={props.disabled}
          aria-label={props.placeholder}
          maxLength={props.maxLength}
          pattern={props.pattern}
          readOnly={props.readOnly}
          autoComplete={props.autoComplete}
          onChange={event => props.onChange(event.target.value)}
          onBlur={event => props.onBlur(event.target.value)}
          onFocus={props.onFocus}
          className={props.className}
          {...hooks.hook(props.automationHook)}
          tabIndex={props.onClick ? "-1" : "0"}
        />
      </ConditionalWrap>

      {props.icon && (
        <IconContainer>{props.icon}</IconContainer>
      )}
    </Box>
  </div>
)))`
  height: 50px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 ${props => props.theme.spacing(4)};
  border-radius: 8px;
  text-overflow: ellipsis;

  ${props => props.disabled && `
    color: ${props.theme.colors.input.disabledText};
    background-color: ${props.theme.colors.input.disabledBackground};
  `}
  
  &:hover,
  &:focus {
    ${props => props.theme.focusStyle}
  }

  ${props => props.icon && `
    /* avoid collision with icon */
    padding-right: 50px;
  `}

  ${props => props.withBorder && `
    border: 1px solid ${props.theme.colors.input.border};
  `}


  ${props => props.hasError && !props.disabled && `
    border-color: ${props.theme.colors.input.error};
  `}

  ${props => props.hideSpinControls && `
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      appearance: none;
      margin: 0;
    }
  `}

  &::placeholder {
    color: ${props => props.theme.colors.input.placeholderText};
  }
`;

Input.propTypes = propTypes;
Input.defaultProps = defaultProps;

export default Input;
